<template>
  <div class="p-8" style="overflow: hidden;">
    <div class="selection-container">
      <!-- Botões de Status no topo -->
      <div class="buttons-row">
        <v-btn
          v-for="(option, index) in options"
          :key="index"
          @click="onOptionSelected(option.value)"
          :class="[option.class, {'active-button': activeOption === option.value}]"
          fab x-small outlined dense
          class="status-button"
        />
        <v-btn
          @click="onOptionSelected(0)"
          :class="['status-button', 'x-button', { 'active-x-button': activeOption === 0 }]"
          fab
          x-small
          dense
          elevation="0"
        />
      </div>
    </div>

    <p v-if="showAttentionMessage" class="attention-text">
      Atenção: deve votar a verde ou amarelo em, <br />pelo menos, {{ this.eventMinDuration }} dias seguidos.
    </p>

    <div class="fade"></div>

    <!-- Renderizar um calendário por mês -->
    <div class="calendar-container container">
      <div v-for="(dates, month) in groupedDatesSorted" :key="month" class="calendar-wrapper">
        <div class="v-picker__body v-picker__body--no-title">
          <CustomCalendar
            :key="eventDatesKey"
            :available-dates="allowedDates(month)"
            :selectedColors="localSelectedColors"
            :eventTimes="getLocalEventTimes"
            v-model="selectedDate"
            @update:modelValue="onDateSelected"
            @save-times="saveTimes"
            :currentMonth="getMonthFromData(month)"
            :currentYear="getYearFromData(month)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Events from '@/api/Events.js';
import CustomCalendar from '@/components/ui/CustomCalendar.vue'

// ---------- Helpers de tempo ----------
const toMinutes = (hhmm) => {
  const [H, M] = (hhmm || '00:00').split(':').map(Number);
  return H * 60 + (isNaN(M) ? 0 : M);
};
const minutesToHHMM = (m) => {
  const H = String(Math.floor(m / 60)).padStart(2, '0');
  const M = String(m % 60).padStart(2, '0');
  return `${H}:${M}`;
};

// Normaliza por dia: remove duplicados, ordena, e em "por dia" colapsa para 1 slot
const normalizeDaySlots = (timesByDate, isHours) => {
  const out = {};
  Object.entries(timesByDate || {}).forEach(([date, list]) => {
    const byTime = new Map();
    (list || []).forEach(s => {
      const time = (s.time || '00:00').slice(0, 5);
      byTime.set(time, { time, value: s.value ?? null });
    });
    let unique = Array.from(byTime.values()).sort((a,b) => toMinutes(a.time) - toMinutes(b.time));
    if (!isHours) {
      const lastDefined = unique.map(x => x.value).filter(v => v != null).pop() ?? null;
      unique = [{ time: '00:00', value: lastDefined }];
    }
    out[date] = unique;
  });
  return out;
};

export default {
  components:{ CustomCalendar },

  props: {
    eventId: { type: String, required: true },
    eventDates: { type: Array, required: true },
    eventDuration: { type: String },     // 'hours' | 'days' | 'weeks'
    toggleHours: Boolean,
    selectedColors: { type: Object, default: () => ({}) },
    eventTimes: { type: Object, default: () => ({}) },
    showAttention: { type: Boolean, default: false }
  },

  data() {
    return {
      eventDatesKey: 0,
      localToggleHours: this.toggleHours,
      options: [
        { value: 1, class: 'red-button' },
        { value: 2, class: 'yellow-button' },
        { value: 3, class: 'green-button' },
      ],
      selectedOption: 3,
      activeOption: 3,
      selectedDate: null,
      eventMinDuration: 0,
      localEventDates: [...this.eventDates],
      localSelectedColors: { ...this.selectedColors },
      localEventTimes: { ...this.eventTimes },
      groupedDates: {},
    };
  },

  methods: {
    reloadEventDates(){ this.eventDatesKey++ },

    async saveTimes() {
      try {
        if (!this.selectedDate) {
          console.warn("⚠ Nenhuma data selecionada para salvar.");
          return;
        }

        const isHours = this.eventDuration === 'hours';
        let votes = [];

        if (isHours) {
          const events = (this.localEventTimes[this.selectedDate] || [])
            .slice()
            .sort((a,b) => toMinutes(a.time) - toMinutes(b.time));

          votes = events.map((event) => {
            const vote_start = `${this.selectedDate}T${event.time}:00`;
            const mStart = toMinutes(event.time);
            const mEnd   = mStart + 30; // slot exato de 30'
            const endHHMM = minutesToHHMM(Math.min(mEnd, 23*60+59));
            const vote_end = `${this.selectedDate}T${endHHMM}:00`;
            return { vote_start, vote_end, vote_value: this.selectedOption };
          });
        } else {
          votes.push({
            vote_start: `${this.selectedDate}T00:00:00`,
            vote_end:   `${this.selectedDate}T23:59:00`,
            vote_value: this.selectedOption
          });
        }

        const params = {
          user_id: this.$root.session.id,
          event_id: this.eventId,
          date: this.selectedDate,
          votes,
          valid_vote: !this.showAttentionMessage
        };

        console.log("📅 Salvando horários:", votes);
        await Events.insertTime(params);

        // Atualiza o estado local
        this.localSelectedColors = {
          ...this.localSelectedColors,
          [this.selectedDate]: this.selectedOption,
        };
        if (this.localEventTimes[this.selectedDate]) {
          this.localEventTimes = {
            ...this.localEventTimes,
            [this.selectedDate]: this.localEventTimes[this.selectedDate].map(s => ({ ...s, value: this.selectedOption }))
          };
        }

        this.reloadEventDates();
        console.log("reloaded.");
      } catch (error) {
        console.error("❌ Erro ao salvar horários:", error);
      }
    },

    getMonthFromData(data){ return parseInt(data.split('-')[1]) - 1 },
    getYearFromData(data){ return parseInt(data.split('-')[0]) },

    groupDatesByMonth(dates) {
      if (!Array.isArray(dates)) {
        console.warn('⚠ eventDates não é um array:', dates);
        this.groupedDates = {};
        return;
      }
      this.groupedDates = dates.reduce((acc, date) => {
        const month = date.slice(0, 7);
        if (!acc[month]) acc[month] = [];
        acc[month].push(date);
        return acc;
      }, {});
    },

    allowedDates(month) {
      return this.groupedDates[month] || [];
    },

    async loadEvent() {
      try {
        const { data: event } = await Events.get(this.eventId);
        console.log('✅ Evento carregado:', event);

        const isHours = (this.eventDuration || event.duration_type) === 'hours';
        this.eventMinDuration = event.min_duration;

        const dates = [];
        const timesByDate = {};

        // 1) Gerar slots a partir dos intervalos
        if (event.intervals && event.intervals.length) {
          event.intervals.forEach(interval => {
            const start = new Date(interval.start_datetime);
            const end   = new Date(interval.end_datetime);

            if (isHours) {
              let current = new Date(start);
              while (current <= end) {
                const dateStr = `${current.getFullYear()}-${String(current.getMonth()+1).padStart(2,"0")}-${String(current.getDate()).padStart(2,"0")}`;
                if (!dates.includes(dateStr)) {
                  dates.push(dateStr);
                  timesByDate[dateStr] = [];
                }
                const hh = String(current.getHours()).padStart(2,'0');
                const mm = String(current.getMinutes()).padStart(2,'0');
                const timeStr = `${hh}:${mm}`;
                if (!timesByDate[dateStr].some(t => t.time === timeStr)) {
                  timesByDate[dateStr].push({ time: timeStr, value: null });
                }
                current = new Date(current.getTime() + 30 * 60000);
              }
            } else {
              let cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
              const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
              while (cur <= last) {
                const dateStr = `${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,"0")}-${String(cur.getDate()).padStart(2,"0")}`;
                if (!dates.includes(dateStr)) dates.push(dateStr);
                timesByDate[dateStr] = [{ time: '00:00', value: null }]; // 1 slot por dia
                cur.setDate(cur.getDate() + 1);
              }
            }
          });
        }

        // 2) Aplicar votos
        if (event.votes && event.votes.length) {
          event.votes.forEach(vote => {
            const [dateStrRaw, startRaw] = vote.vote_start.split(' ');
            const endRaw = vote.vote_end.split(' ')[1];

            const startHHMM = (startRaw || '00:00').slice(0,5);
            const endHHMM   = (endRaw   || '00:00').slice(0,5);
            const dateStr   = dateStrRaw;

            if (!timesByDate[dateStr]) return;
            const list = timesByDate[dateStr];

            if (isHours) {
              const startM = toMinutes(startHHMM);
              let endM = toMinutes(endHHMM);
              if (endHHMM === '23:59') endM = 23 * 60 + 59; // incluir último slot
              list.forEach(slot => {
                const m = toMinutes(slot.time);
                if (m >= startM && m <= endM) {
                  slot.value = vote.vote_value;
                }
              });
            } else {
              if (list.length === 0) list.push({ time: '00:00', value: null });
              list[0].value = vote.vote_value;
            }
          });
        }

        // 3) Normalizar (dedupe/ordenar/colapsar por dia)
        const normalized = normalizeDaySlots(timesByDate, isHours);

        // 4) State
        this.localEventDates  = dates;
        this.localEventTimes  = normalized;
        this.groupDatesByMonth(dates);

        // 5) Cores por dia (1 vez, sobre dados normalizados)
        this.localSelectedColors = {};
        Object.entries(this.localEventTimes).forEach(([date, times]) => {
          if (!isHours) {
            this.$set(this.localSelectedColors, date, times[0]?.value ?? null);
          } else {
            const allEqual = times.length > 0 && times.every(t => t.value === times[0].value);
            this.$set(this.localSelectedColors, date, allEqual ? times[0].value : null);
          }
        });

        // 6) Refresh quando sem votos
        if (!event.votes || event.votes.length === 0) {
          this.localEventTimes     = { ...this.localEventTimes };
          this.localSelectedColors = { ...this.localSelectedColors };
          this.localEventDates     = [ ...this.localEventDates ];
          this.$nextTick(() => { this.eventDatesKey++; });
        }
      } catch (error) {
        console.error('❌ Erro ao carregar evento:', error);
        this.localEventDates = [];
        this.localEventTimes = {};
        this.localSelectedColors = {};
      }
    },

    onOptionSelected(option) {
      this.selectedOption = option;
      this.activeOption = option;
      this.$emit('option-selected', option);
    },

    onDateSelected(date) {
      if (!this.toggleHours) {
        // por dia
        this.$emit('date-selected', { date, option: this.selectedOption, autoSelectAll: true });

        this.localSelectedColors = { ...this.localSelectedColors, [date]: this.selectedOption };

        if (this.localEventTimes[date]) {
          this.localEventTimes = {
            ...this.localEventTimes,
            [date]: this.localEventTimes[date].map((event) => ({ ...event, value: this.selectedOption }))
          };
        }
      } else {
        // por horas
        this.$emit('date-selected', { date, option: this.selectedOption, autoSelectAll: false });
      }
      this.$emit('date-changed', date);
    }
  },

  watch: {
    toggleHours(newVal) {
      this.localToggleHours = newVal;
    },
    eventDates: {
      immediate: true,
      handler(newDates) {
        this.localEventDates = [...newDates];
        this.groupDatesByMonth(newDates);
      }
    },
    selectedColors: {
      immediate: true,
      deep: true,
      handler(newColors) {
        this.localSelectedColors = { ...newColors };
      }
    },
    eventTimes: {
      immediate: true,
      deep: true,
      handler(time) {
        const isHours = (this.eventDuration) === 'hours';
        // normaliza o que vem de fora para não reintroduzir duplicados
        const normalized = normalizeDaySlots(time || {}, isHours);
        this.localEventTimes = normalized;
      }
    },
  },

  created() {
    if (this.localEventDates.length === 0) {
      this.loadEvent();
    }
  },

  computed: {
    showAttentionMessage() {
      const votedDays = Object.values(this.localSelectedColors).filter(v => v === 2 || v === 3).length;
      return votedDays < this.eventMinDuration;
    },
    processedEventDates() {
      return this.localEventDates ? [...this.localEventDates] : [];
    },
    getLocalEventTimes() {
      // belt-and-braces: devolve sempre normalizado
      const isHours = (this.eventDuration) === 'hours';
      return normalizeDaySlots(this.localEventTimes || {}, isHours);
    },
    groupedDatesSorted() {
      const sortedEntries = Object.entries(this.groupedDates)
        .sort((a, b) => new Date(a[0] + '-01') - new Date(b[0] + '-01'));
      return Object.fromEntries(sortedEntries);
    }
  },
};
</script>

<style scoped>
.buttons-row {
  margin-bottom: 20px;
  margin-left: 50px;
  margin-top: 20px;
}
.status-button {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  margin-right: 10px;
}
.red-button { background-color: #fa9a9a; color: #fa9a9a }
.yellow-button { background-color: #f5e720; color: #f5e720; }
.green-button { background-color: #cbd8c7; color: #cbd8c7; }
.x-button { background-color: #e9cfed; }
.active-x-button { border: 2px solid black !important; }
.active-button { color:black; border: 2px solid black !important; }
.v-btn { margin-right: 10px; }

.left-column { background-color: #e9cfed; }
.custom-date-picker .v-date-picker-table { background-color: transparent !important; border: none; }
.custom-date-picker .v-date-picker-day {
  color: black !important; font-size: 14px !important;
  width: 32px !important; height: 32px !important; border-radius: 8px !important;
  background-color: white !important; margin: 4px;
}
.custom-date-picker .v-date-picker-day--selected,
.custom-date-picker .v-date-picker-day--active { background-color: #6c63ff !important; color: white !important; }
.custom-date-picker .v-date-picker-day--current { background-color: #fa9a9a !important; color: white !important; }
.custom-date-picker .v-picker__title { display: none !important; }

.calendar-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%; 
  min-width: 382px;
  gap: 40px; 
  overflow: auto; 
  height: 100%;
}
.calendar-wrapper { display: block; width: 100%; justify-content: flex-start; margin-bottom: 20px; }
.calendar-wrapper:last-child { margin-bottom: 200px; }

::-webkit-scrollbar-track { background-color: rgba(255, 255, 255, 0.1); border-radius: 10px; }
::-webkit-scrollbar { width: 8px; background: rgba(255, 255, 255, 0.1); border-radius: 10px; }
::-webkit-scrollbar-thumb { background: #b0b0b0; border-radius: 10px; }

.selection-container {
  display: flex; position: sticky; top: 0; background-color: #e9cfed; z-index: 10;
  align-items: center; justify-content: space-between;
}
.fade {
  position: fixed;
  background: linear-gradient(to bottom, rgba(233, 207, 237, 1), rgba(233, 207, 237, 0.2) 100%, transparent 100%);
  width: 100%; height: 20px; z-index: 10;
}
.icon-switch-container { display: flex; align-items: center; gap: 4px; }

@media (max-width: 798px) {
  .calendar-container { align-items: center !important; }
}

.attention-text { color: red; margin-top: 20px; text-align: center; font-size: 10px; }
</style>
